import { useState } from 'react';
import classNames from 'classnames';
import { MappingEditor } from './MappingEditor';
import { Dialog } from 'components/Dialogs/Dialog';
import { FlowStepMappingEditorInformation } from '@decisions/server/DecisionsFramework/Design/Flow';
import { useEditComplete } from 'components/property-editors/PropertyGrid/hooks/useEditComplete';
import { EditCompleteType } from '@decisions/server/DecisionsFramework/Design/Properties';
import { ErrorBoundary } from 'react-error-boundary';
import { MappingEditorErrorFallback } from './MappingEditorError/MappingEditorErrorFallback';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContext';
import { DebounceComponent } from 'components/DebounceComponent';
import { Spinner } from 'components/Spinner';

// import './MappingEditorDialog.less'

export interface IMappingEditorDialogProps {
  editorInfo: FlowStepMappingEditorInformation;
  onClose: () => Promise<void>;
  forCustom?: boolean;
  mappingContext?: MappingContext;
}

/**
 * Dialog wrapper around the Mapping Editor.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function MappingEditorDialog({ editorInfo, onClose, forCustom, mappingContext }: IMappingEditorDialogProps) {
  const [editingContextId, setEditingContextId] = useState(''); // ref because we don't need a re-render on it's update.
  const { editComplete } = useEditComplete({ editingContextId });
  const [submitting, setSubmitting] = useState(false);

  // can only save since this change:
  // https://eir.decisions.com/core/platform/-/merge_requests/17509/diffs#ba544d9be98c7a0977b61cc4956387085bf9a69c_57_59
  const onSave = async () => {
    setSubmitting(true);
    const succeeded = await editComplete(EditCompleteType.Saved);
    if (succeeded) {
      // wait for any work that needs to happen behind this dialog to complete
      await onClose();
    } else {
      // something went wrong. re-enable the button and give the user another try
      // hopefully will never happen, but...
      setSubmitting(false);
    }
  };

  const [isInput] = useState(editorInfo.ForInput); // TODO either/or dialog stuff
  const titleText = editorInfo.Name // TODO string.substr is deprecated!
    ? `${editorInfo.Name.substr(0, editorInfo.Name.indexOf('('))}: ${editorInfo.FlowStepName}`
    : 'Mapping Editor';

  return (
    <Dialog
      open={true}
      onClose={onSave}
      styles={{ height: 1064, width: 1000, minWidth: 900 }}
      isFullScreen={true}
      disableFullScreenAction={false}
      className={getClassName()}
      dialogTitle={{ titleText }}
      dialogActions={{
        actions: [
          // because we now update the step context w/ mapping changes live, there is no longer a "Cancel" operation
          {
            name: 'Ok',
            onClick: onSave,
            isDisabled: submitting,
          },
        ],
      }}
      keyboard={{ trapTabbing: true, autoFocusFirst: false }}
    >
      <>
        <ErrorBoundary FallbackComponent={MappingEditorErrorFallback}>
          <MappingEditor
            flowSessionId={editorInfo.FlowSessionId}
            flowStepId={editorInfo.FlowStepId}
            forInput={isInput}
            setEditingContextId={setEditingContextId}
            forCustom={forCustom}
            mappingContext={mappingContext}
          />
        </ErrorBoundary>
        {submitting && (
          <DebounceComponent delay={1000}>
            <div className="dp-spinner__centering-backdrop-container">
              <Spinner />
            </div>
          </DebounceComponent>
        )}
      </>
    </Dialog>
  );
}

function getClassName() {
  return classNames('dp-mapping-editor-dialog');
}
